import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Loader2Icon, LogInIcon } from 'lucide-react'
import { useId, useState } from 'react'
import { useForm } from 'react-hook-form'

import { authApi } from '@/api/auth'
import { ApiError } from '@/api/errors'
import { homePathFor } from '@/auth/guards'
import { parseLoginSearch, portalNextPath } from '@/auth/redirect'
import { ensureSessionResolved } from '@/auth/session'
import { useAuthStore } from '@/auth/store'
import { MagicLinkForm } from '@/components/auth/magic-link-form'
import { AuthLayout } from '@/components/auth-layout'
import { FieldError, FormError } from '@/components/field-error'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { browserLocale, msg, setCurrentLocale } from '@/i18n'
import { portalLocale } from '@/i18n/portal-language'

interface LoginForm {
  email: string
  password: string
}

export const Route = createFileRoute('/login')({
  validateSearch: parseLoginSearch,
  beforeLoad: async ({ search }) => {
    await ensureSessionResolved()
    const { status, user } = useAuthStore.getState()
    if (status === 'authenticated' && user) {
      throw redirect({ to: search.redirect ?? homePathFor(user.kind) })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  // Lot 34 : la personne n'est pas encore connue — la langue de son navigateur.
  setCurrentLocale(portalLocale(browserLocale()))
  const { redirect: redirectTo, mode } = Route.useSearch()
  // 1.69.0 : qui arrive d'une page du portail (le lien d'un courriel, un
  // favori) reçoit d'abord un lien ; l'équipe garde son mot de passe.
  const [useLink, setUseLink] = useState(
    mode === 'lien' || portalNextPath(redirectTo) !== undefined,
  )
  const [email, setEmail] = useState('')
  return (
    <AuthLayout title={msg().auth.signInTitle}>
      {useLink ? (
        <MagicLinkForm
          email={email}
          onEmailChange={setEmail}
          nextPath={portalNextPath(redirectTo)}
          onUsePassword={() => {
            setUseLink(false)
          }}
        />
      ) : (
        <PasswordForm
          email={email}
          onEmailChange={setEmail}
          redirectTo={redirectTo}
          onUseLink={() => {
            setUseLink(true)
          }}
        />
      )}
    </AuthLayout>
  )
}

function PasswordForm({
  email,
  onEmailChange,
  redirectTo,
  onUseLink,
}: {
  email: string
  onEmailChange: (email: string) => void
  redirectTo: string | undefined
  onUseLink: () => void
}) {
  const navigate = useNavigate()
  const signIn = useAuthStore((state) => state.signIn)
  const [formError, setFormError] = useState<string | null>(null)
  const emailId = useId()
  const passwordId = useId()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({ defaultValues: { email, password: '' } })
  const t = msg().auth
  const emailField = register('email', {
    required: t.emailRequired,
    pattern: { value: /.+@.+\..+/, message: t.emailInvalid },
  })

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    try {
      const session = await authApi.login({
        email: values.email.trim(),
        password: values.password,
      })
      signIn(session)
      await navigate({ to: redirectTo ?? homePathFor(session.user.kind) })
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setFormError(t.wrongCredentials)
      } else if (error instanceof ApiError && error.status === 429) {
        setFormError(t.tooManyAttempts)
      } else if (error instanceof ApiError) {
        setFormError(error.message)
      } else {
        setFormError(t.signInFailed)
      }
    }
  })

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      <FormError>{formError}</FormError>

      <div className="space-y-2">
        <Label htmlFor={emailId}>{t.email}</Label>
        <Input
          id={emailId}
          type="email"
          autoComplete="username"
          // eslint-disable-next-line jsx-a11y/no-autofocus -- page dédiée à un formulaire unique
          autoFocus
          placeholder="vous@oyenga.ca"
          data-testid="login-email-input"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? `${emailId}-error` : undefined}
          {...emailField}
          onChange={(event) => {
            onEmailChange(event.target.value)
            void emailField.onChange(event)
          }}
        />
        <FieldError id={`${emailId}-error`}>{errors.email?.message}</FieldError>
      </div>

      <div className="space-y-2">
        <Label htmlFor={passwordId}>{t.password}</Label>
        <Input
          id={passwordId}
          type="password"
          autoComplete="current-password"
          data-testid="login-password-input"
          aria-invalid={!!errors.password}
          aria-describedby={errors.password ? `${passwordId}-error` : undefined}
          {...register('password', { required: t.passwordRequired })}
        />
        <FieldError id={`${passwordId}-error`}>{errors.password?.message}</FieldError>
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={isSubmitting}
        data-testid="login-submit-btn"
      >
        {isSubmitting ? <Loader2Icon className="animate-spin" /> : <LogInIcon />}
        {t.signIn}
      </Button>

      {/* Lot 30 : un client peut se passer de mot de passe ; l'équipe garde le sien. */}
      <div className="border-t pt-4 text-center text-sm text-muted-foreground">
        {t.clientQuestion}{' '}
        <button
          type="button"
          className="font-medium text-primary underline-offset-2 hover:underline"
          onClick={onUseLink}
          data-testid="login-magic-link-btn"
        >
          {t.magic.request}
        </button>
      </div>
    </form>
  )
}
